import { DocumentModel } from '@components/FileUpload/types';
import { Coordinate } from '@components/Map/Map';
import { FacilityDetails } from '@common/facilityDetails';
import {
  CarrierDetailsBlacklist,
  CarrierDetailsMaxLiability,
  CarrierEquipment,
  CarrierHighwayRuleAssociationViewModel,
  CarrierReactivationRequestCacheItemT,
  Comment,
  Contact,
  Rating,
} from '@store/carriers/carrierDetails/types';
import {
  LTLRateQuoteRequest,
  LTLRateQuoteResponse,
  LTLReferenceNumber,
} from '@store/ltl/types';
import { Comments } from '@typeDefinitions/comments';
import { ICustomerContact } from '@typeDefinitions/contactTypes';
import { FeatureFlagCustomer } from '@typeDefinitions/featureFlagTypes';
import { TrackingStopWindow } from '@typeDefinitions/loadTypes';
import type { LTLCatalogs } from '@typeDefinitions/ltlTypes';
import {
  OperationType,
  OperationTypeV4,
} from '@typeDefinitions/operationTypes';
import { IOrderViewModel } from '@typeDefinitions/orderTypes';
import { availableServices } from '@typeDefinitions/postingTypes';
import { LastUsedTopStopModel } from '@typeDefinitions/stopTypes';
import { insuranceProduct } from '@views/order/components/carrier/loads/LoadInfo/LoadSureHelpers';

export type { LTLCatalogs };

export type MenuItemType = {
  label: string;
  value: string;
  disabled?: boolean | undefined;
};

export type loadStatus = {
  id: number;
  status: string;
};

export type BounceReasonCode = {
  id: number;
  name: string;
};

export type BounceCarrierPayload = {
  loadId: number;
  reasonId: number;
  notes: string | null;
};

export type ILatLon = {
  Coords: {
    Lat: string;
    Lon: string;
  };
  Errors: {
    Code: number;
    Description: string;
    LegacyErrorCode: number;
    Type: number;
  }[];
};

export type UserData = {
  id: number;
  name: string;
  createdDateUTC?: Date;
};

export type UserDataViewLocksComposite = {
  lockedBy: UserData | undefined | null;
  viewedBy: UserData[];
};

export type LoadSureState = {
  loadId: number | null;
  cargoValue: number | null;
  equipment: string | null;
  commodity: string | null;
  otherCommodity: string | null;
  otherEquipment: string | null;
  descriptionOfGoodsInsured: string | null;
  confirmation: boolean;
  allRiskInsuranceQuote: insuranceProduct | null;
  gapInsuranceQuote: insuranceProduct | null;
  isGap: boolean;
  isAllRisk: boolean;
  allRiskToken: string | null;
  gapToken: string | null;
  insuredCompanyName: string | null;
  insuredCompanyPhone: string | null;
  insuredCompanyAddress1: string | null;
  insuredCompanyCity: string | null;
  insuredCompanyState: string | null;
  insuredCompanyZip: string | null;
  insuredCompanyCountry: string | null;
  customerContactEmail: string | null;
  isDirty: number[];
  callback: (() => void) | null;
  successCallback: (() => void) | null;
};

export type OrderState = {
  // activeCarrierTab: number;
  order: IOrderViewModel;
  orderIdUrl?: string;
  quoteIdUrl?: string;
  quoteId?: string;
  loading: boolean;
  isError: boolean;
  activeTab: TabState;
  topStops: LastUsedTopStopModel[];
  lastStops: LastUsedTopStopModel[];
  mode: string;
  mapCords: Coordinate[];
  orderFormError: Record<string, string[] | undefined> | null;
  orderFormErrorPerSection: {
    customerInformation: number;
    loads: number;
    instructionsAndCosts: number;
  };
  orderFormSoftErrors: Record<string, string>;
  availableServices: availableServices[];
  managePostLoad: IManagePostLoad;
  isSaving: boolean;
  isSubmittingPickup: boolean;
  isReadOnly: boolean;
  nearbyCarriers: NearbyCarriersReturn[];
  isCreate: boolean;
  isStopsEdited: boolean;
  mappedErrorSection: Record<string, string> | null;
  orderTemplates: OrderTemplate[];
  carrierContactLockdownEnabled: boolean | undefined;
  isTemplateMode: boolean;
  matched: boolean;
  vendorFalveyInsuranceError: string;
  copiedOrderIdUrl?: string;
  copiedOrderId?: string;
  orderLockEnabled: boolean | undefined;
  forceUnlockOrderEnabled: boolean | undefined;
  isMobile: boolean;
  is1080p: boolean;
  viewedBy: UserData[];
  lockedBy?: UserData | null;
  forceTransferToUserId?: number | null;
  forceTransferToName?: string | null;
  resetDirty: boolean;
};

export type TabState = {
  index: number;
  type: TabType;
};

export enum TabType {
  load = 'load',
  vendor = 'vendor',
}

export type CustomerState = {
  customer: CustomerDetails;
  loading: boolean;
};

export type FeatureFlagCustomerState = {
  featureFlagCustomer: FeatureFlagCustomer;
  loading: boolean;
  isError: boolean;
};

export type PostLoadState = {
  postLoad: IPostLoad;
  isError: boolean;
  loading: boolean;
};
export type OrderDocumentState = {
  loading: boolean;
  documents: DocumentModel[];
};

export type Commodity = {
  id: number;
  name: string;
};

export type EquipmentType = {
  id: number;
  name: string;
};
export interface CustomerDetails {
  id: number;
  name: string;
  pointOfSaleInstructions: string | null;
  trackingXID: string;
  operationsRep: string | null;
  operationsRepId: number | null;
  capacityGroupId: number | null;
  capacityGroupLeader: number | null;
  creditLimit: number;
  available: number;
  status: string;
  paymentTerms: string;
  thirdParty: boolean;
  edi: boolean;
  accountManager: string;
  accountManagerId: number;
  salesManager: string;
  salesManagerId: number;
  freightContacts: ICustomerContact[];
  billingContacts: ICustomerContact[];
  specialInstructions: string;
  bolInstructions: null | string;
  trackLoads: boolean;
  requiredTrackingService: number;
  trackingVisibilityProvider: unknown; // You may replace 'any' with a specific type if applicable
  chargeCreditCardFee: boolean;
  pcMilerVersion: string; // You may replace 'any' with a specific type if applicable
  metadata: string[]; // You may replace 'any' with a specific type if applicable
  invoiceMethod: string;
  assignAccountManagerAsOrderRep?: boolean;
  showCustomerCurrencyTypeEnabled?: boolean;
  negativeCreditAllowance: number | null;
  defaultEdiTimeZone: string | null;
}

export type FalveyInsurance = {
  id: number | undefined;
  shipmentUUID: string;
  ltlQuoteId: string | number;
  loadId: number | undefined;
  falveyShipmentID: string;
  shippingSumInsured: number;
  shippingCurrency: string;
  quotePremium: number;
  quoteServiceFee: number;
  certificateNumber: string;
  policyNumber: string;
  waybillAdditional: string;
  certificatePDFLink: string;
  createdDate: string;
  modifiedDate: string | null;
  createdBy: number;
  modifiedBy: number | null;
};

export type LoadTrackingT = {
  operationType: OperationType;
  id: string;
  loadId: number;
  trackingProviderId?: number | undefined;
  latitude?: number | undefined;
  longitude?: number | undefined;
  city?: string | undefined;
  state?: string | undefined;
  locationTime: Date;
  trackingSource: string | undefined;
};

export interface LTLReferenceNumberCollection {
  operationType: OperationTypeV4;
  referenceNumbers: LTLReferenceNumber[];
}

export type ClaimViewModel = {
  orderId: number;
  loadId: number;
  agent: string;
  customer: string;
  loadNumber: string;
  description: string | null;
  status: string | null;
  commodity: string;
  cause: null;
  claimNumber: number | null;
  claimDate: string;
  occurredDate: string;
  occurredDateFormatted: string | null;
  claimDateFormatted: string | null;
  claimValue: number | null;
  closedDate: string;
  closedFormattedDate: string | null;
  carrierPaid: null;
  atgPaid: null;
  agentPaid: null;
  insurancePaid: null;
  amountDeclined: null;
  referenceNumber: null;
  comments: Comments[];
};

export interface LtlQuoteViewModelLegacy {
  operationType: OperationTypeV4;
  quoteId?: string;
  id: string | number | undefined;
  quoteDate: string;
  carrierName?: string;
  customerName?: string;
  customerId?: number;
  carrierTotal: number;
  customerTotal?: number;
  pickupAccessorials?: string[];
  deliveryAccessorials?: string[];
  shipmentAccessorials?: string[];
  pickupAccessorialDescription?: string;
  deliveryAccessorialDescription?: string;
  shipmentAccessorialDescription?: string;
  maxNewLiability?: number;
  maxUsedLiability?: number;
  transitDays?: string;
  serviceType?: string;
  ltlQuoteLoadItems?: LtlQuoteLoadItemViewModel[];
  terminalInfo?: LtlTerminalInfoViewModel;
  movementType?: string;
  rateQuote: LTLRateQuoteResponse;
  rateRequest: LTLRateQuoteRequest | undefined;
  ltlInsurance: FalveyInsurance | null;
  quoteParametersId?: number;
  isStackable?: boolean;
  linearFeet?: string;
  isLinearFeetOverridden?: boolean;
}

export interface LtlQuoteParametersViewModel {
  accessorials: LtlQuoteAccessorialViewModel[];
  // New LTL accessorial schema's stable codes for the currently selected
  // accessorials. Sent alongside the legacy GUID-based `accessorials` list
  // so the backend can resolve the corresponding ltlAccessorialId itself
  // and write both schemas — codes are shared between the legacy and new
  // schemas, so this can always be populated regardless of whether the
  // LtlAccessorialsSchemaRefactorRead flag is enabled.
  accessorialCodes?: string[];
  assignedOrderId?: number;
  createdByName: string;
  customerId?: number;
  customerName?: string;
  deliveryAccessorials?: string[];
  destinationCity: string;
  destinationCountry: string;
  destinationState: string;
  destinationZip: string;
  id: number;
  isLinearFeetOverridden: boolean;
  isStackable: boolean;
  linearFeet: number;
  loadItems?: LtlQuoteLoadItemViewModel[];
  ltlInsuranceQuote: FalveyInsuranceQuote | null;
  ltlQuotes?: LtlQuoteViewModel[];
  movementType?: string;
  operationType: OperationTypeV4;
  originCity: string;
  originCountry: string;
  originState: string;
  originZip: string;
  pickupAccessorials?: string[];
  pickupDate?: string;
  provider: string;
  quoteCount?: number;
  quoteDate: string;
  quoteRequestLocator: string;
  shipmentAccessorials?: string[];
  totalQuoteRequests?: number;
  originCustomerAddressId?: string | null;
  destinationCustomerAddressId?: string | null;
  // False when the user manually overrode the city/state for the entered ZIP.
  originAddressValidated?: boolean;
  destinationAddressValidated?: boolean;
  value: boolean;
  willExpireSoon: boolean;
}

export interface LtlQuoteResultsViewModel {
  // Running totals as of this poll, not deltas since the last poll.
  successCount: number;
  failureCount: number;
  carriersQuoteCompletionCount: number;
  isComplete: boolean;
  quotes: LtlQuoteViewModel[];
  failedQuotes: LtlFailedQuoteViewModel[];
}

export interface LtlFailedQuoteViewModel {
  id?: number;
  quoteParametersId?: number;
  providerTransactionId?: string;
  sequence?: number;
  carrierName?: string;
  scac?: string;
  carrierScacId?: number;
  carrierId?: number;
  serviceLevelTypeId?: number;
  pricingRequestTypeId?: number;
  pricingRequestType?: string;
  serviceTypeCode?: string;
  serviceTypeDisplayName?: string;
  carrierResponse?: string;
  quoteDate?: string;
  message?: string;
  messageDetails?: string;
  resolution?: string;
  information?: string[];
}

export interface LtlQuoteViewModel {
  carrierId?: number;
  carrierName?: string;
  carrierNotes?: string;
  carrierQuoteId?: string;
  expirationDate?: string | null;
  carrierTotal: number;
  customerId?: number;
  customerTotal?: number;
  estimatedDeliveryDate?: string;
  id: number;
  lineItems?: LtlQuoteLineItemViewModel[];
  ltlInsurancePolicy: FalveyInsurancePolicy | null;
  ltlQuoteId?: string;
  maxNewLiability?: number;
  maxUsedLiability?: number;
  movementType?: string;
  operationType: OperationTypeV4;
  orderId?: number;
  pricingRequestType?: string;
  providerTransactionId?: string;
  quoteDate: string;
  scac?: string;
  sequence?: number;
  serviceTypeCode?: string;
  serviceTypeDisplayName?: string;
  terminalInfo?: LtlTerminalInfoViewModel;
  transitDays?: string;
}

export interface LtlQuoteAccessorialViewModel {
  id: number;
  accessorialId: string;
  quoteParametersId: number;
  accessorialName: string;
  accessorialDescription: string;
  isDelivery: boolean;
  isPickup: boolean;
  isGlobal: boolean;
}

// New-schema types (ltl.Accessorial / ltl.QuoteParametersAccessorial)
export interface LtlAccessorialTypeViewModel {
  id: number;
  description: string;
}

export interface LtlAccessorialCatalogItem {
  id: number;
  code: string;
  displayName: string;
  displayInSelector: boolean;
  displayOrder: number | null;
  accessorialTypeId: number;
}

export interface QuoteParametersAccessorialViewModel {
  quoteParametersId: number;
  accessorial: {
    id: number;
    code: string;
    displayName: string;
    displayInSelector: boolean;
    accessorialType?: LtlAccessorialTypeViewModel;
  };
}

export interface LtlApiRequestSummaryViewModel {
  id: number;
  quoteId: number;
  endpoint: string;
  responseTimeMs: number;
  apiRequestTypeDescription: string;
  createdBy: string;
  createdDate: Date;
}

export interface LtlApiRequestDetailsViewModel {
  id: number;
  request: string;
  response: string;
}

export interface FalveyInsuranceQuote {
  id: number;
  quoteParametersId: number;
  declaredValue: number;
  shippingSumInsured: number;
  premium: number;
  serviceFee: number;
  createdDate: string;
  modifiedDate: string | null;
  createdBy: number;
  modifiedBy: number | null;
}

export interface FalveyInsurancePolicy {
  id: number;
  shipmentUUID: string;
  ltlQuoteId: string;
  loadId: number;
  falveyShipmentID: string;
  shippingCurrency: string;
  premium: number;
  serviceFee: number;
  certificateNumber: string;
  policyNumber: string;
  waybillAdditional: string;
  certificatePDFLink: string;
  createdDate: string;
  modifiedDate: string | null;
  createdBy: number;
  modifiedBy: number | null;
}

export interface LtlQuoteLineItemViewModel {
  operationType: OperationTypeV4;
  id: number;
  quoteId: string;
  type: string;
  description: string;
  quantity: number;
  carrierRate: number;
  customerRate?: number;
}

export interface LtlTerminalInfoViewModel {
  operationType: OperationTypeV4;
  pickupTerminal?: LtlTerminalViewModel;
  deliveryTerminal?: LtlTerminalViewModel;
}
export interface LtlQuoteLoadItemViewModel {
  quoteHazmatDetail?: LtlQuoteHazmatDetailsViewModel | null;
  charge?: null | number;
  class?: string;
  pcf?: string;
  description?: string;
  dimensions?: string;
  displayDimensions?: string;
  hazmat: boolean;
  hazmatDescription?: null | string;
  hazmatItems?: null | string;
  height?: null | number;
  heightInches?: number;
  id: string | number | undefined;
  item?: string;
  length?: null | number;
  lengthInches?: number;
  loadItemHazmatDetail?: LtlQuoteHazmatDetailsViewModel;
  operationType: OperationTypeV4;
  parentQuoteLoadItemId?: number;
  pieces?: number;
  quantity: number;
  quoteLoadItemBreakdown?: LtlQuoteLoadItemViewModel[] | null;
  suggestedClass?: null | string;
  type?: string;
  unitType?: string;
  volume?: null | number;
  weight: number | null;
  width?: null | number;
  widthInches?: number;
  _isBreakdown?: boolean;
  _hasBreakdown?: boolean;
  nmfc?: string;
}

export interface LtlQuoteHazmatDetailsViewModel {
  operationType: OperationTypeV4;
  unNumber?: string;
  packingGroup?: string;
  hazardClass?: string;
  piece?: string;
  contactNumber?: string;
  ltlQuoteLoadItemId: string;
  contactName: string;
}

export interface LtlTerminalViewModel {
  operationType: OperationTypeV4;
  terminalName?: string;
  address1?: string;
  address2?: string;
  city?: string;
  postalCode?: string;
  state?: string;
  countryCode?: string;
  contactName?: string;
  contactPhone?: string;
  contactTollFree?: string;
  contactFax?: string;
  contactEmail?: string;
  carrierName?: string;
  scac?: string;
}

export interface ILoadComments {
  city: string | null;
  comment: string;
  id: number;
  latitude: number | null;
  longitude: number | null;
  milesRemaining: number | null;
  name: string;
  objectId: number;
  operationType: OperationType;
  state: string | null;
  temperature: number | null;
  thumbnail: string | null;
  timestamp: string;
  type: string | null;
  userId: number;
  visibleToCustomer: boolean;
  unitOfTemperature: string | null;
}
export interface EdiUpdate {
  shipmentStatus: string | null;
  appointmentStatus: string | null;
  date: string;
  appointmentNumber: string | null;
  timeZone: string;
  stopId: number;
  statusReason: string;
  loadId: number;
  city: string;
  auto214: boolean;
  temperature: null;
  state: string;
  note: null;
  latitude: null;
  longitude: null;
}
export interface ICustomerPayment {
  operationType: OperationTypeV4;
  amount: number | null;
  description: string;
  paidDate: Date | string | null;
  referenceNum: string;
  checkNumber: string;
  type: string;
  status: string;
  id: number | null;
}

export interface ediLog {
  shipmentId?: string;
  tenderType?: string;
  transactionId?: string;
  type?: string;
  timestamp?: string;
  response?: string;
  event?: {
    description?: string;
    code?: string;
    category?: string;
  };
  status?: {
    description?: string;
    code?: string;
    category?: string;
  };
  address?: null;
  stopNumber?: number;
  eventTime?: string;
  eventTimeZone?: string;
  note?: string;
}

export interface EdiLogState {
  ediLogs: ediLog[];
  loading: boolean;
  isError: boolean;
}

export type HazmatDetails = {
  operationType: OperationTypeV4;
  unNumber: string;
  packingGroup: string;
  hazardClass: string;
  piece: string;
  contactNumber: string;
  loadItemId: number;
  contactName: string;
};

// Mixed-commodity sub-item of a load item. The order API omits operationType
// on saved rows, so an undefined value means "unchanged".
export type LoadItemBreakdown = {
  id: number;
  operationType?: OperationTypeV4;
  description: string;
  class: null | string;
  nmfc: null | string;
  weight: null | number;
  pieces: null | number | string;
  hazmat: boolean;
  loadItemHazmatDetail?: null | HazmatDetails;
};

export type PickUpDropOffItem = {
  createdBy: number | null;
  operationType: OperationTypeV4;
  id: number;
  ltlQuoteLoadItemId?: string;
  loadId: number;
  nmfc: null | string;
  class: null | string;
  item: string;
  description: string;
  quantity: number;
  charge: null | string;
  pickUpId: number | null;
  dropOffId: number | null;
  weight: number;
  uom: 'lb(s)' | string;
  volume: null | number;
  hazmat: boolean;
  heightInch: null | number;
  lengthInch: null | number;
  widthInch: null | number;
  height: null | string;
  length: number;
  width: null | string;
  pieces: null | string;
  descriptionWithParanthesis: string;
  displayDimensions: string;
  loadItemHazmatDetail?: null | HazmatDetails;
  loadItemBreakdowns?: null | LoadItemBreakdown[];
  createdDate: null | string;
  historyContext: null | string;
  modifiedDate: null | string;
  modifiedBy: null | string;
  load: null | string;
  dropOff: null | string;
  pickUp: null | string;
  suggestedClass: null | string;
  hazmatItems: null | string;
  hazmatDescription: null | string;
  type: string | null;
  pcf: string;
};

export type LoadTenderState = {
  loadTender?: ILoadTender;
  loading: boolean;
  isError: boolean;
};

export interface ILoadTender {
  shipmentId: string;
  customerId: number;
  customer: string;
  rate: string;
  equipment: string;
  transportationMethod: string;
  stops: IStops[];
  miles: number;
}

export type CarrierContactT = {
  id: number;
  objectid: number;
  objecttype?: string;
  name: string;
  phone1: string;
  extension1?: string | null;
  phone2?: string;
  extension2?: string | null;
  fax?: string;
  email?: string;
  type?: string;
  status?: string;
  createdDate?: string;
  modifiedDate?: string;
  mailerEmails: boolean;
  password?: string;
  username?: string;
  phone?: string;
  verificationStatus: number;
  emailVerified: boolean;
  phoneVerified: boolean;
  ownershipSource: number;
};

export interface IStops {
  stopNum: number;
  stopId: number;
  weight: number;
  volume: string;
  bol: string;
  ponumber: string;
  transactionNumber?: number;
  requestedDate: string;
  requestedTime?: string;
  earliestRequestedDate: string;
  earliestRequestedTime?: string;
  latestRequestedDate: string;
  latestShipTime?: string;
  pallets: number;
  numItems: number;
  name: string;
  address1: string;
  address2: string;
  phone: string;
  referenceNumber?: string;
  commodity?: string;
  weightUOM?: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  code: string;
  pickUp: boolean;
  dropOff: boolean;
  contact: CarrierContactT;
  appointmentNumber?: number;
  appointmentDate?: string;
  description?: string;
  instructions?: string;
  note?: string;
  reason: string;
  items?: string;
}

export interface ICreditCardPayment {
  emailSettings: {
    to: string[];
    body: string;
    subject: string;
  };
  fee: boolean;
}
interface Address {
  line1: string;
  line2: string | null;
  city: string;
  state: string;
  zip: string;
  name: string | null;
  phone: string | null;
  phoneAreaCode: string | null;
  phoneSevenDigit: string | null;
  code: string | null;
  country: string | null;
  contact: string | null;
  contactLastName: string | null;
  contactFirstName: string | null;
}

export interface Carrier {
  id: number;
  name: string;
  contacts: CarrierContactT[];
  dot: string;
  mc: string;
  address: Address;
  rating: number;
  numRatings: number;
  status: string;
  carrierActionPlan: boolean;
  officeRating: number;
  isActive: boolean;
  hasExpiredCargoInsurance: boolean;
  hasExpiredLiabilityInsurance: boolean;
  cargoExpiration: string;
  liabilityExpiration: string;
  warnings: string[];
  advancesAllowed: boolean;
  mode: string;
  blacklisted: boolean;
  firstLoad: boolean;
  corporatePhoneNumber: string;
  factors: boolean;
  fallouts: number;
  totalLoads: number;
  ltlEnabled: boolean;
  transfloEnabled: boolean;
  brokerAuthority: boolean;
  currencyOptionEnabled: boolean;
  requireMacropointTracking: boolean | null;
  shipperVerifyMCDotNumber: boolean | null;
  carrierInsightViewModels: CarrierInsightViewModel[] | [] | undefined;
  hasWatchDogAlerts: boolean | null;
  hasIdentityAlerts: boolean | null;
  hasHwyReeferRestriction: boolean | null;
  canBypassHwyReeferRestriction: boolean | null;
  hasReeferInsurance: boolean | null;
  highwayUrl: string | null;
  failingHighwayRuleCodingsCount: number | null;
  carrierReactivationRequestCacheItemViewModel: CarrierReactivationRequestCacheItemT | null;
  isHighwayEnabled: boolean;
  // Absent until VNEXT-15528 deploys. Missing or null means the carrier has no owner.
  carrierOwnerUserId?: number | null;
  carrierOwnerUserName?: string | null;
}

export type CarrierInsightViewModel = {
  insightId: number;
  title: string;
  message: string;
};

type CarrierInsuranceAddress = {
  id: number;
  enabled: boolean;
  countryCode: string;
  physicalAddress1: null | string;
  physicalAddress2: null | string;
  physicalCity: null | string;
  physicalZip: null | string;
  physicalStateOrProvince: null | string;
  mailingAddress1: null | string;
  mailingAddress2: null | string;
  mailingCity: null | string;
  mailingStateOrProvince: null | string;
  mailingZip: null | string;
  factoringCompanyId: null | string;
  achRouting: null | string;
  achAccount: null | string;
  factoringCompany: null | string;
};

export type CarrierInsuranceDetail = {
  overallMaxLiabilityClass: string;
  id: number;
  currentContactId: number;
  name: string;
  taxId: string;
  mc: string;
  dot: string;
  billingTerms: string;
  remitTo: null | string;
  emailCopy: null | string;
  liabilityInsuranceCompany: string;
  liabilityLimit: number;
  liabilityPolicyNumber: string;
  liabilityExpiration: string;
  cargoInsuranceCompany: string;
  cargoLimit: number;
  cargoPolicyNumber: string;
  cargoExpiration: string;
  workersCompInsuranceCompany: null | string;
  workersCompLimitEachAccident: number;
  workersCompPolicyNumber: null | string;
  workersCompExpiration: null | string;
  workersCompLimitDisease: number;
  workersCompLimitPolicy: number;
  contractDocument: null | string;
  contractSignedDate: null | string;
  contractSignedBy: null | string;
  documents: null | string;
  needs1099: boolean;
  factor: boolean;
  conditionalActionPlanReceived: boolean;
  status: string;
  createdDate: string;
  modifiedDate: string;
  falloutCount: number;
  mode: null | string;
  allowAdvances: boolean;
  alreadySetup: boolean;
  accountingSysId: string;
  vendorHold: boolean;
  taxIdVerificationDate: null | string;
  smartWayCertified: boolean;
  reeferInsurance: boolean;
  hazmatCertified: boolean;
  safetyRating: null | string;
  safetyWarnings: null | string;
  brokerAuthority: boolean;
  commonAuthority: boolean;
  contractAuthority: boolean;
  isFavorite: boolean;
  officeRating: number;
  numRatings: number;
  numberOfLoads: number;
  ltlEnabled: boolean;
  comments: Comment[];
  contacts: Contact[];
  ratings: Rating[];
  equipments: CarrierEquipment[];
  maxLiabilities: CarrierDetailsMaxLiability[];
  overallMaxUsedLiability: number | null;
  overallMaxNewLiability: number | null;
  blacklist: CarrierDetailsBlacklist | null;
  corporatePhoneNumber: string;
  ltlTerminalLink: null | string;
  ltlRateNote: null | string;
  transfloEnabled: boolean;
  vendorHoldReason: null | string;
  vendorHoldDescription: null | string;
  usaAddress: CarrierInsuranceAddress;
  canAddress: CarrierInsuranceAddress;
  averageRating: number;
  paymentTermDays: number;
  hasExpiredCargoInsurance: boolean;
  hasExpiredLiabilityInsurance: boolean;
  carrierHighwayRuleAssociations: CarrierHighwayRuleAssociationViewModel[];
  intrastateFullyPassed: boolean;
  interstateFullyPassed: boolean;
  highValueCargoFullyPassed: boolean;
  refrigeratedFullyPassed: boolean;
  caRefrigeratedFullyPassed: boolean;
  hazmatFullyPassed: boolean;
  canadianCrossBorderFullyPassed: boolean;
  coBrokerFullyPassed: boolean;
};

export type CarrierFeatures = {
  triumphPayCurrency: boolean;
  triumphPayInvoice: boolean;
  triumphPayLoad: boolean;
};

export type CarrierState = {
  carrier: Carrier[];
  featureFlags: CarrierFeatures[];
  playlists: Playlist[];
  loading: boolean;
  isError: boolean;
};

export type CustomerAddress = {
  id?: string | null;
  customerId: number;
  name?: string | null;
  specialInstructions?: string | null;
  description?: string | null;
  addressLine1?: string | null;
  addressLine2?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  openTimeFrom?: Date | null | string;
  openTimeTo?: Date | null | string;
  contactName?: string | null;
  contactPhone?: string | null;
  contactEmail?: string | null;
  createdOn?: Date | null | string;
  countryCode?: string | null;
  internalNote?: string | null;
  drivingDirections?: string | null;
  isDefault?: string | null;
  isDeleted?: boolean;
  pickupHoursFrom?: string | null;
  pickupHoursTo?: string | null;
  pickupHoursClosed?: boolean | null;
  pickupHoursNotes?: string | null;
  receivingHoursFrom?: string | null;
  receivingHoursTo?: string | null;
  receivingHoursClosed?: boolean | null;
  receivingUsePickupHours?: boolean | null;
  receivingHoursNotes?: string | null;
  appointmentType?: string | null;
  schedulingMethod?: string | null;
  facilityDetails?: FacilityDetails | null;
  // customer?: Customer | null;
  // customerAddressHistories?: CustomerAddressHistory[] | null;
};

export interface Accessorial {
  id: string;
  name: string;
  isGlobal: boolean;
  isDeleted: boolean;
  sortOrder: number | null;
  isDelivery: boolean;
  isPickup: boolean;
}

export interface AddressAccessorial {
  addressId: string;
  accessorialId: string;
  accessorialType: 1 | 2; // 1 = pickup, 2 = delivery
  operationType: string | null;
  accessorial: Accessorial;
}

export type CustomerAutoCompleteObject = {
  id: number | null;
  name: string;
  office: string | null;
  accountManager: string | null;
  status: 'Customer';
  defaultOriginZipCode: string | null;
  defaultOriginCity: string | null;
  defaultOriginState: string | null;
  defaultOriginCountryCode: string | null;
  defaultOriginAddressId: string | null;
  customerAddressAccessorials: AddressAccessorial[] | null;
};

export type LoadCommentResponse = {
  city: string | null;
  comment: string;
  id: number;
  latitude: number | null;
  longitude: number | null;
  milesRemaining: number | null;
  name: string;
  objectId: number;
  operationType: OperationType;
  state: string | null;
  temperature: number | null;
  thumbnail: string | null;
  timestamp: string;
  type: string | null;
  userId: number;
  visibleToCustomer: boolean;
};

export type ODataApiResponse = {
  '@odata.context': string;
  '@odata.count': number;
  value: LTLCatalogs[];
};

export enum FlagType {
  Red = 'Red Flag',
  Blue = 'Blue Flag',
  Green = 'Green Flag',
  Yellow = 'Yellow Flag',
  Orange = 'Orange Flag',
  BillingHold = 'Billing Hold',
  BillingNote = 'Billing Note',
  EFSIssued = 'EFS Issued',
  Flagged = 'Flagged',
  GetPaperwork = 'Get Paperwork',
  Hold = 'Hold',
  HotLoad = 'Hot Load',
  Issue = 'Issue',
  MissingPaperwork = 'Missing Paperwork',
  NeedsApproval = 'Needs Approval',
  NeedsPaperwork = 'Needs Paperwork',
  RefundRequest = 'Refund Request',
  TenderUpdated = 'Tender Updated',
  Uploaded = 'Uploaded',
  None = '',
  AIEscalated = 'Escalated',
  NeedsReview = 'Needs Review',
}

export type DisputedOrderFlagT = {
  clearedBy: string | undefined | null;
  clearedDate: string | undefined | null;
  disputedDate: string;
  flagId: number;
  id: string;
  operationType: string;
};

export interface CommentViewModel {
  operationType: OperationType;
  id: number;
  objectId: number;
  userId: number;
  name?: string | undefined;
  type?: string | undefined;
  comment?: string | undefined;
  timestamp?: Date | string | undefined;
  thumbnail?: string | undefined;
  createdBy?: string | undefined;
}

export type PostingMode = 'TL' | 'LTL' | 'Partial';

export interface IPostLoad {
  pickUp: string | Date;
  equipment: string;
  origin: string;
  destination: string;
  price: number | null;
  originCity: string;
  originState: string;
  originCountry: string;
  mileage?: number | null;
  destinationCity: string;
  destinationState: string;
  destinationCountry: string;
  dropOff?: string | Date;
  drops: number;
  mode: PostingMode;
  fullTruckload: boolean;
  tags: string[];
  weight: number;
  length: number;
  postingClients?: string[];
  id: null | number;
  loadId: number;
  servicesToPostTo: string[];
  internalNote: string;
  comment: string;
  atgOverride?: string | null;
  datOverride?: string | null;
  tsOverride?: string | null;
  tsSubcodeOverride?: string | null;
  mailerOverride?: string | null;
}

export interface IManagePostLoad {
  id: number;
  loadId: number;
  boardsPostedTo: string[] | null;
  isQuickPost?: boolean;
  origin: string;
  originCity: string;
  originState: string;
  originCountry: string;
  destination: string;
  destinationCity: string;
  destinationState: string;
  destinationCountry: string;
  pickUp: string;
  dropOff: string;
  price: number | null;
  equipment: string;
  comment: string;
  drops: number;
  height?: number | null;
  length: number;
  width?: number | null;
  datePosted?: string;
  reference?: string | null;
  volume?: number | null;
  weight: number;
  tags?: string[] | undefined;
  availableServices?: string[] | null;
  servicesToPostTo: string[] | null;
  loadNumber?: string;
  mode?: PostingMode | null;
  fullTruckload: boolean;
  postedBy?: string;
  mileage: number;
  internalNote: string;
  contactPhone?: string | null;
  originZip?: string | null;
  destinationZip?: string | null;
  boardsPosted?: string;
  tagsList?: string;
  atgOverride?: string | null;
  datOverride?: string | null;
  tsOverride?: string | null;
  tsSubcodeOverride?: string | null;
  mailerOverride?: string | null;
  errors: string[];
}
export interface Playlist {
  id: number;
  userId: number;
  name: string;
  user: string;
  carriers: PlaylistCarrier[];
  carriersCount: number;
  groups: Group[];
  editable: boolean;
  canEdit: boolean;
}

export interface PlaylistCarrier {
  id: number;
  name: string | null;
  mc: string | null;
  dot: string | null;
  status: string | null;
  operationType: OperationTypeV4;
}

interface Group {
  id: number;
  name: string;
  operationType: OperationTypeV4;
  officeName: string;
}
export type RatePayloadType = {
  rating: number;
  comment: string;
  carrierId: number;
  loadId: number;
};

export type AddFlagCommentPayload = {
  id: number;
  comment?: string;
};

export interface TrackingData {
  loadId: number;
  trackingStartDate: string;
  serviceId: number | null;
  trackingStopWindows: TrackingStopWindow[];
  driverPhone: string;
  truckNumberMacro: string;
  trailerNumberMacro: string;
  trackingEmailMacro: string;
  TrackingFrequencyMacro: string;
  truckNumberTruckerTools: string;
  trailerNumberTruckerTools: string;
  minTempTruckerTools: number | null;
  maxTempTruckerTools: number | null;
  tempUomTruckerTools: string;
  eldAssetId: string;
  trackingEmailTruckerTools: string;
  shipperEmailTruckerTools: string;
  brokerEmailTruckerTools: string;
  carrierDispatchEmailTruckerTools: string;
  settings: {
    truckNumber: string;
    trailerNumber: string;
    timeZone: string;
    trackingEmail: string;
    shipperEmail: string;
    brokerEmail: string;
    carrierDispatchEmail: string;
    TrackingFrequency: string;
    minTemp: string;
    maxTemp: string;
    tempUom: string;
    eldAssetId: string;
  };
}

export enum TrackingProviders {
  'Macropoint' = 1,
  'TenFourSystems' = 2,
  'Project44' = 3,
  'FourKites' = 4,
  'Transflo' = 5,
  'Shippeo' = 6,
  'TruckerTools' = 7,
  'Transporeon' = 8,
}

export interface UserInfoModel {
  id: number;
  name: string;
  email: string;
  office: string;
  officeId: number;
  accountingGroup: string;
  phone1?: string;
  phone1Ext?: number | null;
  altPhone?: string;
  alptPhone?: number;
  fax?: string;
}

export interface NearbyCarriersReturn {
  carrier: string;
  mc: string;
  dot: string;
  dropDate: string;
  dropCity: string;
  dropState: string;
  dropZip: string;
  contact: string;
  contactType: string;
  phone: string;
  email: string;
  truckNum: string;
  trailerNum: string;
  carrierId: number;
  equipment1: string;
}

export interface NearbyCarrierRequestViewModel {
  zip: string;
  startDate: string;
  radius?: number;
  equipment: string;
}

export type BaseStatusCode = {
  description: string;
  code: string;
  category: string | null;
};

export type EDIStatusCodesApiResponse = {
  statusCodes: BaseStatusCode[];
  statusReasons: BaseStatusCode[];
  appointmentCodes: BaseStatusCode[];
};

export type LoadEdiStatusUpdatePayload = {
  loadId: number;
  shipmentStatus: string;
  appointmentStatus: string | null;
  statusReason: string;
  stopId: number;
  date: string;
  timezone: string;
};
export interface PcMilerError {
  type: number;
  description: string;
  code?: number;
  legacyErrorCode?: number;
}

export interface PcMilerResponse {
  address: {
    zip?: string | null | number;
    city?: string | null;
    state?: string | null;
    countryAbbreviation?: string | null;
    countryPostalFilter?: number | null;
  };
  errors?: PcMilerError[] | null;
}

export type ShareUnShareCapacityPayload = {
  id: number;
  customerId: number;
  loadId: number;
  orderId: number;
  removeCapacity: boolean;
};

export interface AppointmentReminderModel {
  date: string;
  description: string;
  endTime: string;
  objectIdType: string;
  public: boolean;
  startTime: string;
  time: string;
  title: string;
}

export type GeocodeModel = {
  address: string;
  city: string;
  state: string;
  lat?: number;
  lng?: number;
};

export type OrderTemplate = {
  createdDate: string;
  editable: boolean;
  id: number;
  name: string;
  orderId: number;
  owner: string;
  public: boolean;
};

export type SubmitShipmentResult = {
  code: string;
  message: string;
  thirdPartyBilling: string | null;
  trackingNumber: string;
  providerResponse: string;
  proNumber: string | null;
};

export type CommitQuoteResult = {
  bol: string;
  falveyShipmentID: number;
  shipmentDate: string;
  shippingSumInsured: string;
  shippingSumInsuredWords: string;
  shippingCurrency: string;
  premium: number;
  providerFee: number;
  serviceFee: number;
  certificateNumber: string;
  policyNumber: string;
  certificatePDF: string;
  waybillAdditional: string;
  invoiceNum: string;
  certificatePDFLink: string | null;
  certificatePDFQRCode: string;
  errors?: string[];
};

export type ShareCapacityResult = {
  assignedId: number;
  assignedName: string;
  capacityGroupName: string;
  maxBuyRate: number;
};

export type LoadSearch = {
  loadId: number;
  orderId: number;
  loadNumber: string;
  customerId: number;
  customer: string;
  status: string;
  carrier: string;
  mode: string;
  equipment: string;
  agent: string;
  loadType: string | null;
  coveredBy: string;
  accountManager: string;
  salesManager: string;
  office: string;
  officeId: number;
  originCity: string;
  originState: string;
  destinationCity: string;
  destinationState: string;
  pickup: string;
  pickupTime: string;
  dropoff: string;
  dropoffTime: string;
  trackingNumber: string | null;
  trailerNumber: string | null;
  referenceNum: string | null;
  carrierPay: number;
  customerPay: number | null;
  loadCreatedDate: string;
  loadBilledDate: string | null;
  claim: boolean;
  orderStatus: string;
  carrierContactName: string;
  carrierContactType: string;
  carrierContactPhone: string | null;
  carrierContactEmail: string;
  weight: number;
  pieces: number;
  product: string | null;
};

export interface RevenueSearchData {
  orderId: number;
  numLoads: number;
  numLoadsCovered: number;
  numVendors: number;
  mode: string;
  equipment: string | null;
  referenceNumber: string;
  customerId: number;
  customer: string;
  customerBusinessType: string | null;
  customerCurrencyType: string;
  status: string;
  owner: string;
  agentId: number;
  accountManager: string;
  salesManager: string;
  carrier: string;
  groups: string[];
  customerGroups: string[];
  coveredBy: string;
  carriers: string[];
  office: string;
  officeId: number;
  originCity: string;
  originState: string;
  destinationCity: string;
  destinationState: string;
  pickup: string;
  dropoff: string;
  carrierPay: number;
  customerPay: number;
  adjustments: number;
  vendorPay: number;
  createdDate: string;
  invoicedDate: string;
  paidDate: string | null;
  profit: number;
  margin: number;
  origin: string;
  destination: string;
  bidType: string;
  spotId: string | null;
  spotQuote: string | null;
}

export interface RevenueSearchExportQueued {
  jobId: number;
  status: string;
  statusUrl: string;
  requestedAtUtc: string;
}

export interface RevenueSearchExportStatus {
  jobId: number;
  status: 'Queued' | 'Processing' | 'Completed' | 'Failed' | string;
  requestedAtUtc: string;
  startedAtUtc: string | null;
  completedAtUtc: string | null;
  fileName: string | null;
  fileSizeBytes: number | null;
  rowCount: number | null;
  downloadUrl: string | null;
  errorMessage: string | null;
}

export type FetchPricingForPostingPayload = {
  origination: {
    address: string | null;
    city: string | null;
    state: string | null;
    country: string | null;
    zip: string | null;
  };
  destination: {
    address: string | null;
    city: string | null;
    state: string | null;
    country: string | null;
    zip: string | null;
  };
  equipment: string | null;
  pricingService: number;
};

export type FetchPricingForPostingResponse = {
  avgTotal: number;
  highTotal: number;
  lowTotal: number;
  detailsLink: unknown | null;
};

export type AdjustmentT = {
  amount: number;
  createdBy: number;
  createdDate: string;
  description: string;
  id: number;
  modifiedBy: string | null;
  modifiedDate: string | null;
  order: unknown | null;
  orderId: number;
  retraction: unknown;
  settledDate: string | null;
};

export type TrackingResponse = {
  adjustment: AdjustmentT;
  success: boolean;
  trackingId: string;
  error: string;
  errorCode: string;
  message: string;
  shipperLink: string | null;
};

export interface LoadSearchFilter {
  field: string;
  filterType: 'EQUALS' | 'IN_RANGE' | 'IN';

  values: (string | number)[];
}
interface Sort {
  field: string;
  direction: 'asc' | 'desc';
}

export type PersonFilterField =
  | 'orderrepid'
  | 'coveredbyid'
  | 'accountmanagerid'
  | 'salesmanagerid'
  | 'createdById';

export type PersonFilterPayload =
  | { type: 'condition'; field: PersonFilterField; value: number }
  | {
      type: 'group';
      operator: 'AND' | 'OR';
      children: PersonFilterPayload[];
    };

export interface LoadSearchPayload {
  filters: LoadSearchFilter[];
  sort: Sort;
  pageNumber: number;
  pageSize: number;
  personFilter?: PersonFilterPayload | null;
}

export type LoadSearchRes = {
  data: LoadSearch[];
  totalCount: number;
};

export type V4CatalogApiResponse<T> = {
  errors: {
    code: string;
    message: string;
  }[];
  isFailure: boolean;
  isSuccess: boolean;
  data: T;
};

export type MileageResult = {
  totalPracticalMiles: number;
  totalShortMiles: number;
  stopSegments: {
    practicalMileageToNextStop: number | null;
    shortestMileageToNextStop: number | null;
  }[];
};

export type LaneLocation = {
  city: string;
  state: string;
  zip: string;
  country: string;
  lat: number;
  long: number;
};

export type Lane = {
  origin: LaneLocation;
  destination: LaneLocation;
};

export type LaneHistoryFilters = {
  origin: LaneLocation;
  originRadiusMiles: number;
  destination: LaneLocation;
  destinationRadiusMiles: number;
  equipment: string[];
  timeHorizon: string;
  mode: string;
};
